/**
 * CartAuthGate Manager
 * Handles the non-dismissable lead capture gate for empty cart guest additions.
 */
(function () {
  const STORAGE_KEY = 'aguise_cart_lead_submitted';

  class CartAuthGateManager {
    constructor() {
      this.modal = null;
      this.form = null;
      this.pendingCallback = null;
      this.isSubmitting = false;

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }

    init() {
      this.modal = document.getElementById('CartAuthGateModal');
      this.form = document.getElementById('CartAuthGateForm');

      if (!this.modal || !this.form) return;

      // Intercept keydown to strictly block Escape key
      window.addEventListener(
        'keydown',
        (e) => {
          if (this.isOpen() && (e.key === 'Escape' || e.keyCode === 27)) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );

      // Handle form submission
      this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }

    isOpen() {
      return this.modal && this.modal.style.display !== 'none';
    }

    /**
     * Determines if the current add-to-cart attempt should be gated.
     * Returns true if user is guest, lead is not submitted, and cart item count is 0.
     */
    async shouldGate() {
      if (window.Shopify && window.Shopify.customerLoggedIn) return false;
      if (localStorage.getItem(STORAGE_KEY) === 'true') return false;

      try {
        const cartResponse = await fetch(`${window.routes?.cart_url || '/cart'}.js`);
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          return cart.item_count === 0;
        }
      } catch (err) {
        console.error('Error fetching cart state:', err);
      }

      return false;
    }

    /**
     * Opens the non-dismissable modal and sets the callback to run after successful lead submission.
     */
    openModal(onSuccessCallback) {
      if (!this.modal) this.init();
      if (!this.modal) return;

      this.pendingCallback = onSuccessCallback;
      this.modal.style.display = 'flex';
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');

      // Focus on first input
      setTimeout(() => {
        const firstInput = this.modal.querySelector('input:not([type="hidden"])');
        if (firstInput) firstInput.focus();
      }, 100);
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.style.display = 'none';
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
    }

    async handleFormSubmit(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (this.isSubmitting) return;

      const errorMsg = document.getElementById('CartAuthFormError');
      if (errorMsg) errorMsg.hidden = true;

      // Validate inputs
      const firstNameInput = this.form.querySelector('#CartAuthFirstName');
      const emailInput = this.form.querySelector('#CartAuthEmail');
      const phoneInputEl = this.form.querySelector('#CartAuthPhone');

      const firstName = firstNameInput?.value.trim() || '';
      const email = emailInput?.value.trim() || '';
      const phoneInput = phoneInputEl?.value.trim() || '';

      if (!firstName || !email || !phoneInput) {
        if (errorMsg) {
          errorMsg.textContent = 'Please fill out all required fields.';
          errorMsg.hidden = false;
        }
        return;
      }

      if (emailInput && !emailInput.checkValidity()) {
        if (errorMsg) {
          errorMsg.textContent = 'Please enter a valid email address.';
          errorMsg.hidden = false;
        }
        return;
      }

      if (phoneInputEl && !phoneInputEl.checkValidity()) {
        if (errorMsg) {
          errorMsg.textContent = 'Please enter a valid 10-digit mobile number.';
          errorMsg.hidden = false;
        }
        return;
      }

      const submitBtn = this.form.querySelector('[type="submit"]');
      const submitText = this.form.querySelector('.cart-auth-modal__submit-text');
      const spinner = this.form.querySelector('.cart-auth-modal__spinner');

      this.isSubmitting = true;
      if (submitBtn) submitBtn.setAttribute('disabled', 'true');
      if (submitText) submitText.classList.add('hidden');
      if (spinner) spinner.classList.remove('hidden');

      const phone = phoneInput ? `+91${phoneInput}` : '';

      try {
        const productName = document.querySelector('h1')?.innerText || window.location.pathname;

        await fetch("https://hook.eu1.make.com/x7kq9l5a1juhn2kvjab793idk7417nc0", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // "X-MAKE-APIKEY": "6N4gFbqGKHV_3TA" 
          },
          body: JSON.stringify({
            name: firstName,
            email: email,
            phone: phone,
            product: productName,
            date: new Date().toISOString()
          })
        }).catch(err => console.error('Webhook error:', err)); // Catch silently so it doesn't block add to cart if webhook fails

        // 2. Mark lead as captured in localStorage
        localStorage.setItem(STORAGE_KEY, 'true');

        // 4. Close modal and execute the pending product addition
        this.closeModal();

        if (typeof this.pendingCallback === 'function') {
          const cb = this.pendingCallback;
          this.pendingCallback = null;
          cb();
        }
      } catch (err) {
        console.error('Cart lead save error:', err);
        localStorage.setItem(STORAGE_KEY, 'true');
        this.closeModal();
        if (typeof this.pendingCallback === 'function') {
          const cb = this.pendingCallback;
          this.pendingCallback = null;
          cb();
        }
      } finally {
        this.isSubmitting = false;
        if (submitBtn) submitBtn.removeAttribute('disabled');
        if (submitText) submitText.classList.remove('hidden');
        if (spinner) spinner.classList.add('hidden');
      }
    }
  }

  window.CartAuthGate = new CartAuthGateManager();
})();
